import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, Container, Stack, TextField, Typography } from '@mui/material';
import { GetAdditionalDocumentRequestsModel, GetSearchRefundModel } from 'domain/usecases';
import { RefundTaggingEnum } from 'main/analytics/gaTagsEnum';
import { getCurrentAccountAdapter } from 'main/adapters/current-account-adapter';
import {
	makeGetAdditionalDocumentRequests,
	makeGetSearchRefund,
	makePostCreateProtocol,
} from 'main/factories/usecases';
import {
	AlertInfo,
	CTAButtonFixed,
	FailToCompleteActionModal,
	MainFooter,
	StyledGrid,
} from 'presentation/components';
import { AlertNdi } from 'presentation/components/AlertNdi';
import { Loading } from 'presentation/components/Loading/Loading';
import { DateFormatEnum, FeaturesEnum, OperationStateTypesEnum } from 'presentation/constants';
import { useContractStatus } from 'presentation/hooks';
import { useFeatureFlag } from 'presentation/hooks/useFeatureFlag';
import { useMediaQueries } from 'presentation/pages/private/hooks';
import { useSendAdditionalDocs } from 'presentation/pages/private/refund/hooks';
import { translations } from 'presentation/translations-new';
import { formatDateString, stringfyDate } from 'presentation/utils';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSendAdditionalDocsDataStore } from '../../store/useSendAdditionalDocsDataStore';
import {
	AutoSearchLoadingDialog,
	ProtocolItemAccordion,
	RefundFinishedModal,
	RefundInfoDrawer,
	RefundNotFoundModal,
} from './components';
import { getFormSchema, FormSchemaValues } from './schema';
import { CustomStyledCardContainer, StyledFooterContainer } from './styles';
import { SearchProtocolProps } from './types';

const {
	advance,
	refundNumber,
	refundNumberPlaceholder,
	refundNumberWithProtocol,
	refundNumberPlaceholderWithProtocol,
	description,
	descriptionHelperText,
	descriptionPlaceholder,
	searchBtn,
	successInfo,
	refundFoundTitle,
	selectProcedure,
	searchWarning,
	searchWarningWithProtocol,
	protocolHelperText,
	protocolHelperTextWithProtocol,
	alertNdi,
} = translations['pt-br'].privateFlow.refund.additionalDocuments.searchProtocol;

const {
	title: refundNotFoundTitle,
	titleWithProtocol: refundNotFoundTitleWithProtocol,
	description2: refundNotFoundDescription2,
	description2WithProtocol: refundNotFoundDescription2WithProtocol,
} = translations['pt-br'].privateFlow.refund.additionalDocuments.refundNotFoundModal;

const {
	title: errorTitle,
	description: errorDescription,
	headerTitle: errorHeaderTitle,
} = translations['pt-br'].privateFlow.refund.additionalDocuments.errorModal;

const {
	title: searchFailedModalTitle,
	description: searchFailedModalDescription,
	headerTitle: searchFailedModalHeaderTitle,
} = translations['pt-br'].privateFlow.refund.additionalDocuments.searchFailedModal;

const {
	tryAgain,
	goToHome,
	multiCancelBenefit,
	cancelContractIn,
	cancelContract,
	refundCanceledContract,
	refundCanceledContractBeneficiary,
	selectAnOption,
	seeMore,
} = translations['pt-br'].commons;

const PAGE_LIMIT = 3;

export function SearchProtocol({ autoSearchReference }: SearchProtocolProps) {
	const additionalDocsState = useSendAdditionalDocsDataStore(state => state.data);
	const setAdditionalDocsState = useSendAdditionalDocsDataStore(state => state.setData);
	const { isEnabled: isViewSupplementaryDocumentsEnabled } = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_REFUND_VIEW_SUPPLEMENTARY_DOCUMENTS,
	);

	const inputLabel = isViewSupplementaryDocumentsEnabled ? refundNumberWithProtocol : refundNumber;
	const inputLabelInternal = isViewSupplementaryDocumentsEnabled
		? refundNumberPlaceholderWithProtocol
		: refundNumber;
	const inputPlaceholder = isViewSupplementaryDocumentsEnabled
		? refundNumberPlaceholderWithProtocol
		: refundNumberPlaceholder;
	const protocolHelperTextToUse = isViewSupplementaryDocumentsEnabled
		? protocolHelperTextWithProtocol
		: protocolHelperText;
	const searchWarningToUse = isViewSupplementaryDocumentsEnabled
		? searchWarningWithProtocol
		: searchWarning;

	const {
		control,
		handleSubmit,
		watch,
		trigger,
		setValue,
		getValues,
		formState: { errors },
	} = useForm<FormSchemaValues>({
		resolver: zodResolver(getFormSchema(isViewSupplementaryDocumentsEnabled)),
		defaultValues: {
			request: additionalDocsState.refundNumber,
			description: additionalDocsState.observations,
		},
	});

	const {
		isValid,
		isPlanAllCancelled,
		isPlanCancelledDatesAreEqual,
		isAnyPlanCancelled,
		contractCancellationDate,
	} = useContractStatus();
	const { handleNextStep, handleGoToHome } = useSendAdditionalDocs();
	const [searchResponseState, setSearchResponseState] = useState<GetSearchRefundModel>();
	const [additionalDocumentsResponseState, setAdditionalDocumentsResponseState] = useState<GetAdditionalDocumentRequestsModel>();
	const [searchRequestState, setSearchRequestState] = useState(OperationStateTypesEnum.NONE);
	const [appliedReferenceState, setAppliedReferenceState] = useState('');
	const [currentLimit, setCurrentLimit] = useState(PAGE_LIMIT);
	const [isLoadingMoreState, setIsLoadingMoreState] = useState(false);
	const [loadMoreErrorState, setLoadMoreErrorState] = useState(false);
	const [showProcedureErrorState, setShowProcedureErrorState] = useState(false);
	const [openInfoDrawerState, setOpenInfoDrawerState] = useState(false);
	const [createDraftRequestState, setCreateDraftRequestState] = useState(
		OperationStateTypesEnum.NONE,
	);
	const [showSearchButtonState, setShowSearchButtonState] = useState(false);
	const [isNotFoundModalOpenState, setIsNotFoundModalOpenState] = useState(false);
	const [isFinishedModalOpenState, setIsFinishedModalOpenState] = useState(false);
	const [showSearchAlertState, setShowSearchAlertState] = useState(false);
	const [selectedProcedureIndex, setSelectedProcedureIndex] = useState<number | null>(null);
	const [isAutoSearchingState, setIsAutoSearchingState] = useState(false);
	const hasAutoSearchedRef = useRef(false);
	const { isTablet } = useMediaQueries();
	const requestValue = watch('request');
	const descriptionValue = watch('description');
	const isRequestFieldDisabled = additionalDocsState.refundNumber !== '';
	const showSearchButton =
		!isRequestFieldDisabled &&
		(isViewSupplementaryDocumentsEnabled ||
			(requestValue || showSearchButtonState));
	const canSendDocs = isViewSupplementaryDocumentsEnabled
		? (additionalDocumentsResponseState?.content?.requests?.length ?? 0) > 0
		: searchResponseState?.content?.canSendDocs;
	const isFirstFocus = useRef(true);

	const descriptionLength = descriptionValue.length;

	const protocolRequests = additionalDocumentsResponseState?.content?.requests ?? [];
	const protocolTotal = additionalDocumentsResponseState?.content?.total ?? 0;
	const hasMoreProtocols = protocolRequests.length < protocolTotal;

	const handleFocusRegisterField = () => {
		if (isFirstFocus.current) {
			setOpenInfoDrawerState(true);
			isFirstFocus.current = false;
		}
		if (!isViewSupplementaryDocumentsEnabled) setShowSearchButtonState(true);
	};

	const searchByReference = async (
		searchValue: string,
	): Promise<GetAdditionalDocumentRequestsModel | undefined> => {
		try {
			setSearchRequestState(OperationStateTypesEnum.SUBMITTING);
			setSelectedProcedureIndex(null);

			if (isViewSupplementaryDocumentsEnabled) {
				const { beneficiary } = getCurrentAccountAdapter();
				const reference = searchValue.trim();
				const requestsResponse = await makeGetAdditionalDocumentRequests().get({
					userCode: beneficiary.cdUsuario,
					reference,
					page: 0,
					limit: PAGE_LIMIT,
				});
				const requests = requestsResponse?.content?.requests ?? [];

				setSearchRequestState(OperationStateTypesEnum.DONE);

				if (requests.length === 0) {
					setIsNotFoundModalOpenState(true);
					return undefined;
				}

				setAdditionalDocumentsResponseState(requestsResponse);
				setAppliedReferenceState(reference);
				setCurrentLimit(PAGE_LIMIT);

				if (requestsResponse.content.total === 1) {
					setSelectedProcedureIndex(0);
				}

				return requestsResponse;
			} else {
				const response = await makeGetSearchRefund().get({
					request: searchValue.trim(),
				});
				setSearchResponseState(response);
				setSearchRequestState(OperationStateTypesEnum.DONE);

				if (!response) {
					setIsNotFoundModalOpenState(true);
					return undefined;
				}

				if (!response.content?.canSendDocs) {
					setIsFinishedModalOpenState(true);
					return undefined;
				}
			}
		} catch (error) {
			setSearchRequestState(OperationStateTypesEnum.ERROR);
		} finally {
			if (additionalDocsState.refundNumber)
				setAdditionalDocsState({ ...additionalDocsState, refundNumber: '' });
			if (showSearchAlertState) setShowSearchAlertState(false);
		}

		return undefined;
	};

	const handleSearchProtocol = async () => {
		const isValidField = await trigger('request');
		if (!isValidField) return;
		await searchByReference(requestValue);
	};

	useEffect(() => {
		if (!autoSearchReference || !isViewSupplementaryDocumentsEnabled) return;
		if (hasAutoSearchedRef.current || isRequestFieldDisabled) return;
		hasAutoSearchedRef.current = true;

		const runAutoSearch = async () => {
			setValue('request', autoSearchReference);
			setIsAutoSearchingState(true);

			const requestsResponse = await searchByReference(autoSearchReference);
			const [singleRequest] = requestsResponse?.content?.requests ?? [];

			if (requestsResponse?.content?.total === 1 && singleRequest) {
				const selectedRequest = String(singleRequest.request);
				await createDraftAndAdvance({
					selectedRequest,
					requestCode: selectedRequest.slice(-7),
					observations: getValues('description'),
					refundNumber: selectedRequest,
				});
			}

			setIsAutoSearchingState(false);
		};

		void runAutoSearch();
	}, [autoSearchReference, isViewSupplementaryDocumentsEnabled]);

	const handleLoadMore = async () => {
		try {
			setIsLoadingMoreState(true);
			const { beneficiary } = getCurrentAccountAdapter();
			const newLimit = currentLimit + PAGE_LIMIT;
			const requestsResponse = await makeGetAdditionalDocumentRequests().get({
				userCode: beneficiary.cdUsuario,
				reference: appliedReferenceState,
				page: 0,
				limit: newLimit,
			});
			setAdditionalDocumentsResponseState(requestsResponse);
			setCurrentLimit(newLimit);
		} catch {
			setLoadMoreErrorState(true);
		} finally {
			setIsLoadingMoreState(false);
		}
	};

	const createDraftAndAdvance = async ({
		selectedRequest,
		requestCode,
		observations,
		refundNumber: refundNumberToSave,
	}: {
		selectedRequest: string;
		requestCode?: string;
		observations: string;
		refundNumber: string;
	}) => {
		try {
			setCreateDraftRequestState(OperationStateTypesEnum.SUBMITTING);
			const { content } = await makePostCreateProtocol().post({
				observations: '',
				request: selectedRequest,
				requestCode,
			});

			setAdditionalDocsState({
				...additionalDocsState,
				protocol: content.protocol,
				protocolCode: content.protocolCode,
				requestCode: content.requestCode,
				request: content.request,
				observations,
				refundNumber: refundNumberToSave,
			});
			handleNextStep();
		} catch (error) {
			setCreateDraftRequestState(OperationStateTypesEnum.ERROR);
		}
	};

	const handleCreateDraft = async ({
		description: descriptionForm,
		request: requestForm,
	}: FormSchemaValues) => {
		if (additionalDocsState.refundNumber) {
			setAdditionalDocsState({
				...additionalDocsState,
				observations: descriptionForm,
				refundNumber: requestForm,
			});
			handleNextStep();
			return;
		}

		const hasSearchResult = isViewSupplementaryDocumentsEnabled
			? protocolRequests.length > 0
			: !!searchResponseState?.content?.requestCode;
		if (!hasSearchResult) {
			setShowSearchAlertState(true);
			return;
		}

		const mustSelectProcedure =
			isViewSupplementaryDocumentsEnabled &&
			protocolRequests.length > 0 &&
			selectedProcedureIndex === null;
		if (mustSelectProcedure) {
			setShowProcedureErrorState(true);
			return;
		}

		const selectedRequest: string =
			isViewSupplementaryDocumentsEnabled && selectedProcedureIndex !== null
				? String(protocolRequests[selectedProcedureIndex].request)
				: requestForm.trim();

		const requestCodeForPost: string | undefined =
			isViewSupplementaryDocumentsEnabled && selectedProcedureIndex !== null
				? selectedRequest.slice(-7)
				: searchResponseState?.content?.requestCode;

		await createDraftAndAdvance({
			selectedRequest,
			requestCode: requestCodeForPost,
			observations: descriptionForm,
			refundNumber: isViewSupplementaryDocumentsEnabled ? selectedRequest : requestForm,
		});
	};

	const getHelperText = () => {
		if (additionalDocsState.refundNumber !== '') return '';
		return errors.request?.message ?? protocolHelperTextToUse;
	};

	const cancelledContractLabel = (() => {
		if (isPlanAllCancelled) {
			if (isPlanCancelledDatesAreEqual) {
				return `${cancelContractIn}${contractCancellationDate
					? stringfyDate(contractCancellationDate, DateFormatEnum.DATE_FORMAT_8)
					: ''
					}`;
			}
			return cancelContract;
		}

		if (isAnyPlanCancelled) {
			return multiCancelBenefit;
		}

		return null;
	})();

	const refundMessage =
		!isPlanAllCancelled && isAnyPlanCancelled
			? refundCanceledContractBeneficiary
			: refundCanceledContract;

	const IS_SEARCH_REQUEST_LOADING = searchRequestState === OperationStateTypesEnum.SUBMITTING;
	const IS_SEARCH_REQUEST_ERROR = searchRequestState === OperationStateTypesEnum.ERROR;

	const IS_CREATE_DRAFT_REQUEST_LOADING =
		createDraftRequestState === OperationStateTypesEnum.SUBMITTING;
	const IS_CREATE_DRAFT_REQUEST_ERROR = createDraftRequestState === OperationStateTypesEnum.ERROR;

	const handleFormSubmit = () => {
		if (
			isViewSupplementaryDocumentsEnabled &&
			protocolRequests.length > 0 &&
			selectedProcedureIndex === null
		) {
			setShowProcedureErrorState(true);
		}
		if (
			isViewSupplementaryDocumentsEnabled &&
			selectedProcedureIndex !== null &&
			protocolRequests[selectedProcedureIndex]
		) {
			setValue(
				'request',
				String(protocolRequests[selectedProcedureIndex].request),
				{ shouldValidate: true },
			);
		}
		handleSubmit(handleCreateDraft)();
	};

	return (
		<Fragment>
			{!isValid || isAnyPlanCancelled ? (
				<AlertInfo.Root
					severity="warning"
					sx={isTablet ? { margin: '16px 16px 0' } : { padding: '8px 16px 6px' }}>
					<AlertInfo.Description>
						<Typography fontSize={'0.875rem'} component={'span'} fontWeight={700}>
							{cancelledContractLabel}
						</Typography>
						<Typography component={'p'} fontSize={'inherit'}>
							{refundMessage}
						</Typography>
					</AlertInfo.Description>
				</AlertInfo.Root>
			) : null}
			<Container>
				<StyledGrid.Root mt={isTablet ? 0 : 2}>
					<StyledGrid.Item md={12} lg={12}>
						<CustomStyledCardContainer>
							<Stack spacing={1}>
								<Stack>
									<AlertNdi
										title={alertNdi.title}
										message={alertNdi.message}
										buttonTitle={alertNdi.button}
										onClick={() => setOpenInfoDrawerState(true)}
										removeButtonIcon={true}
										marginBottom={'16px'}
									/>
									<Typography
										variant="subtitle1"
										fontWeight={600}
										color="primary">
										{inputLabel}
									</Typography>
								</Stack>
								<Controller
									control={control}
									name="request"
									render={({ field }) => (
										<TextField
											data-tag-id={
												RefundTaggingEnum.DIGITOU_NUMERO_REEMBOLSO_ENVIO_DE_DOCUMENTOS_COMPLEMENTARES
											}
											{...field}
											error={Boolean(errors.request)}
											label={isRequestFieldDisabled ? '' : inputLabelInternal}
											placeholder={isRequestFieldDisabled ? '' : inputPlaceholder}
											helperText={getHelperText()}
											disabled={isRequestFieldDisabled}
											sx={{
												'& .MuiOutlinedInput-root.Mui-disabled': {
													'& .MuiOutlinedInput-notchedOutline': {
														borderStyle: 'solid',
													},
												},
											}}
											onFocus={() => handleFocusRegisterField()}
											onBlur={() => setShowSearchButtonState(false)}
										/>
									)}
								/>
							</Stack>

							{showSearchButton ? (
								<Button
									onClick={handleSearchProtocol}
									fullWidth
									variant="outlined"
									disabled={IS_SEARCH_REQUEST_LOADING}
									sx={{
										'&.Mui-disabled': {
											borderStyle: 'solid',
										},
									}}
									endIcon={
										IS_SEARCH_REQUEST_LOADING ? (
											<Loading size={16} color="inherit" />
										) : null
									}>
									{searchBtn}
								</Button>
							) : null}

							{showSearchAlertState ? (
								<AlertInfo.Root severity="error">{searchWarningToUse}</AlertInfo.Root>
							) : null}

							{!isViewSupplementaryDocumentsEnabled && canSendDocs ? (
								<AlertInfo.Root
									data-tag-id={
										RefundTaggingEnum.ENCONTROU_REEMBOLSO_PARA_ENVIO_DE_DOCUMENTOS_COMPLEMENTARES
									}
									severity="success">
									{successInfo}
								</AlertInfo.Root>
							) : null}

							{isViewSupplementaryDocumentsEnabled && protocolRequests.length > 0 ? (
								<Stack spacing={1} sx={{ mt: 2 }}>
									{protocolTotal === 1 ? (
										<Typography
											variant="subtitle1"
											fontWeight={600}
											color="primary">
											{refundFoundTitle}
										</Typography>
									) : null}
									{protocolTotal > 1 ? (
										<>
											<Typography color="primary" fontWeight={600}>
												{selectProcedure}
											</Typography>
											{showProcedureErrorState ? (
												<Typography variant="body2" color="error">
													{selectAnOption}
												</Typography>
											) : null}
										</>
									) : null}
									{protocolRequests.map((item, index) => (
										<ProtocolItemAccordion
											key={item.request}
											title={`Reembolso #${item.request}`}
											requestedAt={formatDateString(item.registeredDate, 'yyyy-MM-dd', 'dd/MM/yyyy')}
											valueInformed={String(item.totalReportedAmount)}
											providerName={item.executorName}
											fiscalDocNumber={item.document}
											isRefund={protocolTotal === 1}
											error={showProcedureErrorState}
											selected={selectedProcedureIndex === index}
											onSelect={() => {
												setSelectedProcedureIndex(index);
												setShowProcedureErrorState(false);
											}}
										/>
									))}
									{hasMoreProtocols ? (
										<Box display="flex" justifyContent="center">
											<Button
												variant="text"
												color="primary"
												onClick={handleLoadMore}
												disabled={isLoadingMoreState}
												endIcon={
													isLoadingMoreState ? (
														<Loading size={16} color="inherit" />
													) : null
												}>
												{seeMore}
											</Button>
										</Box>
									) : null}
								</Stack>
							) : null}

							<Stack spacing={1}>
								<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
									<Typography color="primary" fontWeight={600}>
										{description}
									</Typography>
									<Typography
										variant="caption"
										color="text.secondary"
										textAlign="end"
										sx={{
											display: 'inline-block',
											paddingX: '12px',
											marginTop: 'auto',
										}}>
										{descriptionLength}/ 1000
									</Typography>
								</Box>
								<Controller
									control={control}
									name="description"
									render={({ field }) => (
										<TextField
											{...field}
											multiline
											rows={4}
											slotProps={{
												htmlInput: {
													maxLength: 1000,
												},
											}}
											label={descriptionPlaceholder}
											placeholder={descriptionPlaceholder}
											error={Boolean(errors.description)}
											helperText={descriptionHelperText}
										/>
									)}
								/>
							</Stack>
						</CustomStyledCardContainer>

						<Stack direction="row" justifyContent="flex-end" mt={2}>
							<CTAButtonFixed.Root>
								<CTAButtonFixed.Action
									data-tag-id={
										RefundTaggingEnum.CLICK_PROSSEGUIR_PARA_ANEXOS_ENVIO_DE_DOCUMENTOS_COMPLEMENTARES
									}
									size="large"
									onClick={handleFormSubmit}
									endIcon={
										IS_CREATE_DRAFT_REQUEST_LOADING ? (
											<Loading size={24} color="inherit" />
										) : null
									}>
									{advance}
								</CTAButtonFixed.Action>
							</CTAButtonFixed.Root>
						</Stack>
					</StyledGrid.Item>
				</StyledGrid.Root>
			</Container>

			<AutoSearchLoadingDialog open={isAutoSearchingState} />
			<RefundInfoDrawer
				open={openInfoDrawerState}
				onClose={() => setOpenInfoDrawerState(false)}
			/>
			<RefundFinishedModal
				open={isFinishedModalOpenState}
				setOpen={setIsFinishedModalOpenState}
			/>
			<RefundNotFoundModal
				open={isNotFoundModalOpenState}
				setOpen={setIsNotFoundModalOpenState}
				title={
					isViewSupplementaryDocumentsEnabled
						? refundNotFoundTitleWithProtocol
						: refundNotFoundTitle
				}
				description2={
					isViewSupplementaryDocumentsEnabled
						? refundNotFoundDescription2WithProtocol
						: refundNotFoundDescription2
				}
				reversedFooter={isViewSupplementaryDocumentsEnabled}
			/>

			<FailToCompleteActionModal
				headerTitle={errorHeaderTitle}
				description={errorDescription}
				title={errorTitle}
				open={IS_CREATE_DRAFT_REQUEST_ERROR}
				setOpen={() => setCreateDraftRequestState(OperationStateTypesEnum.PENDING)}
				primaryActionText={tryAgain}
				secondaryActionText={goToHome}
				fallbackAction={handleGoToHome}
				handleTryAgain={handleSubmit(handleCreateDraft)}
			/>

			<FailToCompleteActionModal
				headerTitle={searchFailedModalHeaderTitle}
				description={searchFailedModalDescription}
				title={searchFailedModalTitle}
				open={IS_SEARCH_REQUEST_ERROR || loadMoreErrorState}
				setOpen={() => {
					setSearchRequestState(OperationStateTypesEnum.PENDING);
					setLoadMoreErrorState(false);
				}}
				primaryActionText={tryAgain}
				secondaryActionText={goToHome}
				fallbackAction={handleGoToHome}
				handleTryAgain={loadMoreErrorState ? handleLoadMore : handleSearchProtocol}
			/>

			<StyledFooterContainer>
				<MainFooter />
			</StyledFooterContainer>
		</Fragment>
	);
}
